import React, { useState, useContext, useEffect } from 'react';
import UdemyInstructorHeader from './UdemyInstructorHeader';
import UdemyInstructorSidebar from './UdemyInstructorSidebar';
import UdemyCourseMetrics from './UdemyCourseMetrics';
import UdemyStudentEngagement from './UdemyStudentEngagement';
import UdemyCourseStatus from './UdemyCourseStatus';
import UdemyRecentReviews from './UdemyRecentReviews';
import UdemyCourseStructure from './UdemyCourseStructure';
import UdemyFilmVideos from './UdemyFilmVideos';
import UdemyCourseSettings from './UdemyCourseSettings';
import UdemyPublishCourse from './UdemyPublishCourse';
import CourseListInstructor from './CourseListInstructor';
import ContentUploader from './ContentUploader';
import { AuthContext } from './AuthContext';
import CourseBuilderForm from './CourseBuilderForm';
import Messages from './Messages';
import api from '../api';

const UdemyInstructorDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [editingCourse, setEditingCourse] = useState(null);
  const [instructorCourses, setInstructorCourses] = useState([]);
  const [statsCourseFilter, setStatsCourseFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const { currentUser } = useContext(AuthContext);

  const fetchCourseDetails = async (courseId) => {
    if (!courseId) return null;

    const response = await api.get(`/courses/${courseId}`);
    const detailedCourse = response.data?.success ? response.data.data : response.data;
    return {
      ...detailedCourse,
      sections: detailedCourse?.sections || [],
      goals: detailedCourse?.goals || [],
      requirements: detailedCourse?.requirements || []
    };
  };

  useEffect(() => {
    const fetchInstructorCourses = async () => {
      try {
        const response = await api.get(`/courses/instructor/${currentUser.id}`);
        const baseCourses = response.data.data || [];

        if (baseCourses.length === 0) {
          setInstructorCourses([]);
          setEditingCourse(null);
          setStatsCourseFilter('all');
          return;
        }

        const detailedCourses = await Promise.all(
          baseCourses.map((course) => fetchCourseDetails(course.id))
        );

        setInstructorCourses(detailedCourses.filter(Boolean));
        setEditingCourse(detailedCourses[0] || null);
        setStatsCourseFilter('all');
      } catch (error) {
        console.error('Error al obtener cursos del instructor:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchInstructorCourses();
    }
  }, [currentUser]);

  const updateCourseInStorage = async (updatedCourse) => {
    try {
      const dataToSend = {
        ...updatedCourse,
        sections: Array.isArray(updatedCourse.sections)
          ? updatedCourse.sections.map((section) => ({
              ...section,
              lectures: Array.isArray(section.lectures) ? section.lectures : []
            }))
          : []
      };

      const response = await api.put(`/courses/${updatedCourse.id}`, dataToSend);

      if (!response.data.success || !response.data.data) {
        throw new Error('Respuesta invalida del servidor');
      }

      const fullUpdatedCourse = {
        ...response.data.data,
        sections: response.data.data.sections || [],
        goals: response.data.data.goals || [],
        requirements: response.data.data.requirements || []
      };

      setInstructorCourses((prev) =>
        prev.map((course) => course.id === fullUpdatedCourse.id ? fullUpdatedCourse : course)
      );
      setEditingCourse(fullUpdatedCourse);
      return fullUpdatedCourse;
    } catch (error) {
      console.error('Detalles del error:', {
        message: error.message,
        response: error.response?.data,
        courseData: updatedCourse
      });
      throw error;
    }
  };

  const handlePublishCourse = async (courseId) => {
    try {
      const response = await api.put(`/courses/${courseId}/publish`);

      if (response.data.success) {
        setInstructorCourses((prev) =>
          prev.map((course) => course.id === courseId ? response.data.data : course)
        );
        setEditingCourse(response.data.data);
        alert('Curso publicado exitosamente!');
      }
    } catch (error) {
      console.error('Detalles del error al publicar:', {
        message: error.message,
        response: error.response?.data
      });

      if (error.response?.data?.errors) {
        alert(`Error al publicar: ${error.response.data.errors.join(', ')}`);
      } else {
        alert('Error al publicar el curso. Verifica la consola para mas detalles.');
      }
    }
  };

  const handleCourseCreated = async (newCourse) => {
    try {
      const completeCourse = {
        ...newCourse,
        instructor_id: currentUser.id,
        status: 'draft',
        sections: [],
        goals: [],
        requirements: [],
        thumbnail_url: '/default-course-thumbnail.jpg'
      };

      const response = await api.post('/courses', completeCourse);
      const createdCourse = response.data.data;

      if (!createdCourse.sections) createdCourse.sections = [];
      if (!createdCourse.goals) createdCourse.goals = [];
      if (!createdCourse.requirements) createdCourse.requirements = [];

      setInstructorCourses((prev) => [...prev, createdCourse]);
      setEditingCourse(createdCourse);
      setActiveTab('structure');
      alert('Curso creado exitosamente! Ahora puedes agregar contenido.');
    } catch (error) {
      console.error('Error al crear curso:', error);
      alert(error.response?.data?.message || 'Error al crear el curso. Por favor, intentalo de nuevo.');
    }
  };

  const handleUploadComplete = async (lectures) => {
    if (!editingCourse) return;

    try {
      const updatedCourse = {
        ...editingCourse,
        sections: editingCourse.sections || []
      };

      let uploadSection = updatedCourse.sections.find((section) => section.title === 'Videos Subidos');
      if (!uploadSection) {
        uploadSection = {
          id: Date.now(),
          title: 'Videos Subidos',
          lectures: []
        };
        updatedCourse.sections.push(uploadSection);
      }

      lectures.forEach((newLecture) => {
        if (!uploadSection.lectures.some((lecture) => lecture.id === newLecture.id)) {
          uploadSection.lectures.push(newLecture);
        }
      });

      const savedCourse = await updateCourseInStorage(updatedCourse);
      setEditingCourse(savedCourse);
    } catch (error) {
      console.error('Error al guardar videos:', error);
      alert('Error al guardar los videos. Por favor, intentalo de nuevo.');
    }
  };

  const handleEditCourseFromList = async (course) => {
    if (!course?.id) return;

    try {
      const detailedCourse = await fetchCourseDetails(course.id);
      if (!detailedCourse) return;

      setInstructorCourses((prev) => {
        const exists = prev.some((item) => item.id === detailedCourse.id);
        if (!exists) {
          return [...prev, detailedCourse];
        }
        return prev.map((item) => item.id === detailedCourse.id ? detailedCourse : item);
      });
      setEditingCourse(detailedCourse);
      setActiveTab('structure');
    } catch (error) {
      console.error('Error al abrir curso para editar:', error);
      alert('No se pudo abrir el curso para editar.');
    }
  };

  const handleViewStatsFromList = async (course) => {
    if (!course?.id) return;

    try {
      const detailedCourse = await fetchCourseDetails(course.id);
      if (!detailedCourse) return;

      setInstructorCourses((prev) => {
        const exists = prev.some((item) => item.id === detailedCourse.id);
        if (!exists) {
          return [...prev, detailedCourse];
        }
        return prev.map((item) => item.id === detailedCourse.id ? detailedCourse : item);
      });
      setEditingCourse(detailedCourse);
      setActiveTab('statistics');
      setStatsCourseFilter(String(detailedCourse.id));
    } catch (error) {
      console.error('Error al abrir estadisticas del curso:', error);
      alert('No se pudieron abrir las estadisticas del curso.');
    }
  };

  const renderDashboardCards = () => (
    <>
      <UdemyCourseMetrics
        instructorCourses={instructorCourses}
        selectedCourseId={statsCourseFilter}
      />
      <UdemyStudentEngagement
        instructorCourses={instructorCourses}
        selectedCourseId={statsCourseFilter}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {editingCourse && <UdemyCourseStatus course={editingCourse} />}
        <UdemyRecentReviews instructorCourses={instructorCourses} />
      </div>
    </>
  );

  const renderContent = () => {
    if (loading) return <div className="text-center py-8">Cargando...</div>;

    switch (activeTab) {
      case 'statistics':
        return renderDashboardCards();
      case 'communication':
        return <Messages />;
      case 'structure':
        return <UdemyCourseStructure course={editingCourse} updateCourse={updateCourseInStorage} />;
      case 'film':
        return <UdemyFilmVideos course={editingCourse} updateCourse={updateCourseInStorage} />;
      case 'upload':
        return editingCourse ? (
          <ContentUploader courseId={editingCourse.id} onUploadComplete={handleUploadComplete} />
        ) : null;
      case 'settings':
        return <UdemyCourseSettings course={editingCourse} updateCourse={updateCourseInStorage} />;
      case 'publish':
        return <UdemyPublishCourse course={editingCourse} updateCourse={updateCourseInStorage} handlePublish={handlePublishCourse} />;
      case 'manageCourses':
        return (
          <CourseListInstructor
            onEditCourse={handleEditCourseFromList}
            onViewStats={handleViewStatsFromList}
          />
        );
      case 'createNewCourse':
        return (
          <CourseBuilderForm
            onSave={handleCourseCreated}
            onCancel={() => setActiveTab('dashboard')}
          />
        );
      default:
        return renderDashboardCards();
    }
  };

  const pageTitle =
    activeTab === 'statistics' ? 'Estadisticas del instructor' :
    activeTab === 'communication' ? 'Comunicacion' :
    activeTab === 'structure' ? 'Estructura del curso' :
    activeTab === 'film' ? 'Filmar videos' :
    activeTab === 'upload' ? 'Subir contenido' :
    activeTab === 'settings' ? 'Configuracion del curso' :
    activeTab === 'publish' ? 'Publicacion' :
    activeTab === 'manageCourses' ? 'Mis Cursos' :
    activeTab === 'createNewCourse' ? 'Crear Nuevo Curso' :
    'Panel de instructor';

  const showCourseSelector = ['structure', 'film', 'upload', 'settings', 'publish'].includes(activeTab);
  const showStatsFilter = ['dashboard', 'statistics'].includes(activeTab);

  return (
    <div className="min-h-screen bg-gray-50">
      <UdemyInstructorHeader activeTab={activeTab} onTabChange={setActiveTab} />
      <div className="flex pt-16">
        <UdemyInstructorSidebar onTabChange={setActiveTab} activeTab={activeTab} />
        <main className="flex-1 p-8 ml-64">
          <h1 className="text-2xl font-bold mb-6">{pageTitle}</h1>

          {showCourseSelector && (
            <div className="mb-6 p-4 bg-white rounded-lg shadow-sm">
              <label className="block text-gray-700 font-medium mb-2">Seleccionar Curso:</label>
              <select
                className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-purple-600"
                value={editingCourse?.id || ''}
                onChange={async (e) => {
                  const selectedId = parseInt(e.target.value, 10);
                  const existingCourse = instructorCourses.find((course) => course.id === selectedId);

                  if (existingCourse?.sections?.some((section) => Array.isArray(section.lectures))) {
                    setEditingCourse(existingCourse);
                    return;
                  }

                  try {
                    const detailedCourse = await fetchCourseDetails(selectedId);
                    if (!detailedCourse) return;

                    setInstructorCourses((prev) =>
                      prev.map((course) => course.id === selectedId ? detailedCourse : course)
                    );
                    setEditingCourse(detailedCourse);
                  } catch (error) {
                    console.error('Error al cargar detalle del curso:', error);
                  }
                }}
              >
                {instructorCourses.length === 0 && <option value="">No tienes cursos</option>}
                {instructorCourses.map((course) => (
                  <option key={course.id} value={course.id}>{course.title}</option>
                ))}
              </select>
              {instructorCourses.length === 0 && (
                <p className="text-red-500 mt-2">Crea un curso primero para acceder a estas funciones.</p>
              )}
            </div>
          )}

          {showStatsFilter && (
            <div className="mb-6 p-4 bg-white rounded-lg shadow-sm">
              <label className="block text-gray-700 font-medium mb-2">Filtrar estadisticas por curso:</label>
              <select
                className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-purple-600"
                value={statsCourseFilter}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setStatsCourseFilter(selectedValue);

                  if (selectedValue === 'all') {
                    return;
                  }

                  const selectedCourse = instructorCourses.find(
                    (course) => String(course.id) === String(selectedValue)
                  );

                  if (selectedCourse) {
                    setEditingCourse(selectedCourse);
                  }
                }}
              >
                <option value="all">Todos los cursos</option>
                {instructorCourses.map((course) => (
                  <option key={course.id} value={String(course.id)}>{course.title}</option>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-2">
                La comision se calcula desde el 20% sobre estudiantes que finalizaron el curso.
              </p>
            </div>
          )}

          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default UdemyInstructorDashboard;
